/* Placing an accepted hero image into a generated article.

   Done in the browser rather than at push time so the admin SEES the real image
   URL in the markdown they are approving — a substitution that happens after the
   preview is a substitution nobody reviewed.

   The templates do NOT agree on how an image is spelt: the blog and client
   case-study layouts use `thumb_image`/`featured_image` blocks holding
   `/images/placeholder.jpg`, while the Triton blueprint uses a bare top-level
   `image:` pointing at `/assets/uploads/<slug>.jpg`. Matching one literal string
   meant an article pushed with a path to a file that was never uploaded, and
   nothing said so. So this works structurally: any image key SHALLOW enough to
   be the article's own lead image, holding a value that is not already a real
   URL. */

/** The stand-in the blog and client case-study templates emit. */
export const HERO_PLACEHOLDER = '/images/placeholder.jpg';

/* Depth matters. `thumb_image.image` sits at indent 2 and is the lead image; the
   `image: ''` inside a content_blocks section sits deeper and is a different
   picture entirely — filling those would put the hero down the whole page. */
const MAX_IMAGE_INDENT = 2;

/* A value worth replacing: empty, a bracketed placeholder, or any site-relative
   path. Generated frontmatter only ever names local paths speculatively — a real
   image is an absolute CDN URL — so a leading "/" means nothing was uploaded.
   An http(s) value is left alone: somebody chose it. */
function isPlaceholderValue(raw: string): boolean {
  const v = raw.trim().replace(/^["']|["']$/g, '');
  if (!v) return true;
  if (/^https?:\/\//i.test(v)) return false;
  return v.startsWith('[') || v.startsWith('/');
}

const frontmatterEnd = (lines: string[]): number => {
  if (lines[0]?.trim() !== '---') return -1;
  for (let i = 1; i < lines.length; i++) if (lines[i].trim() === '---') return i;
  return -1;
};

const quote = (s: string) => JSON.stringify(s ?? '');
const indentOf = (line: string) => line.length - line.trimStart().length;

/** Point the article's lead image at a published URL, and fill its alt text. */
export function applyHeroImage(md: string, url: string, alt: string): string {
  if (!md || !url) return md;
  const lines = md.split('\n');
  const end = frontmatterEnd(lines);
  if (end < 0) return md;

  for (let i = 1; i < end; i++) {
    const line = lines[i];
    const ind = indentOf(line);
    if (ind > MAX_IMAGE_INDENT) continue;
    const pad = ' '.repeat(ind);

    const img = line.match(/^\s*(image|featured_image|thumb_image):\s*(.*)$/);
    if (img && img[2].trim() && isPlaceholderValue(img[2])) {
      lines[i] = `${pad}${img[1]}: ${quote(url)}`;
      continue;
    }
    /* An empty `featured_image:` in the seo block is a key waiting for a value,
       not a parent of one — the parent forms always have an `image:` beneath. */
    if (img && !img[2].trim() && img[1] === 'featured_image' && indentOf(lines[i + 1] ?? '') <= ind) {
      lines[i] = `${pad}featured_image: ${quote(url)}`;
      continue;
    }

    if (!alt) continue;
    const altLine = line.match(/^\s*(image_alt|featured_image_alt):\s*(.*)$/);
    // Alt text a human wrote is never overwritten — only brackets and blanks.
    if (altLine && isPlaceholderValue(altLine[2]) && !altLine[2].trim().startsWith('/')) {
      lines[i] = `${pad}${altLine[1]}: ${quote(alt)}`;
    }
  }
  return lines.join('\n');
}

/** Whether a generated article is still waiting for a lead image. */
export function hasHeroPlaceholder(md: string): boolean {
  const lines = md.split('\n');
  const end = frontmatterEnd(lines);
  if (end < 0) return false;
  for (let i = 1; i < end; i++) {
    if (indentOf(lines[i]) > MAX_IMAGE_INDENT) continue;
    const img = lines[i].match(/^\s*(image|featured_image|thumb_image):\s*(.*)$/);
    if (img && img[2].trim() && isPlaceholderValue(img[2])) return true;
  }
  return false;
}
